// Field help for the operations screens (Installer, Audit log). Keys are the field ids passed to
// <Field help="…">; every rendered label must have one (tests/unit/help.test.ts). The copy follows
// docs/guides/installer.md and docs/guides/admin-app.md#audit.
import type { HelpTopic } from "../topic";

export const OPERATIONS_TOPICS = {
  // ------------------------------------------------------------------ installer: starting a session
  "installer.awsAccountId": {
    title: "AWS account id",
    body: "The customer's 12-digit AWS account the proxy is installed into. It must have a verified link (Linked AWS accounts) before a session can deploy anything there.",
    example: "123456789012. Only one live session is allowed per customer and account; a second one is refused until the first ends.",
    article: "installer",
    section: "linking-an-account",
  },
  "installer.regions": {
    title: "Regions",
    body: "The AWS regions the agent may look at and work in, separated by commas or spaces. Up to 20, as AWS region codes.",
    example: "us-east-1, us-west-2 lets the agent discover both and propose the proxy in either; it will not touch eu-west-1.",
    article: "installer",
    section: "sessions",
  },
  "installer.goal": {
    title: "Goal",
    body: "Install deploys a new Nexus proxy. Upgrade moves an existing installation to the release this Nexus runs, keeping its deployed settings.",
    example: "A customer on last quarter's proxy: choose Upgrade and give its installation id. A new customer: Install.",
    article: "installer",
    section: "sessions",
  },
  "installer.installationId": {
    title: "Installation id",
    body: "For an upgrade: which existing installation to move to the new release. It must be recorded in the same AWS account as this session.",
    example: "8a1f3c2e-5b7d-4e90-a1b2-c3d4e5f60718, from the customer's Installations list.",
    article: "installations",
  },
  "installer.accessMode": {
    title: "Access mode",
    body: "How the installer reaches the customer's account. Pasted STS keys and IAM Identity Center use a person's short-lived credentials, never stored. Bootstrap role needs nothing from you: the installer assumes the linked account's role.",
    example: "A first install where the customer hands over a one-hour session: Pasted STS keys. Upgrades and repeat installs: Bootstrap role.",
    article: "installer",
    section: "access-modes",
  },
  "installer.status": {
    title: "Status",
    body: "Where a session stands. It moves from Awaiting credentials through Discovering, Planning, Awaiting approval, Applying and Verifying to Done. Paused means a budget, turn or time limit was reached and a person must extend or cancel it.",
    example: "Awaiting approval with 1 pending change: open the session and review the change before the agent can continue.",
    article: "installer",
    section: "sessions",
  },
  "installer.pendingChanges": {
    title: "Pending changes",
    body: "Proposed changes waiting for someone to approve or reject them. Nothing is written to the customer's account until a change is approved.",
    example: "2 pending: the Nexus registration and the CloudFormation change set for the proxy stack.",
    article: "installer",
    section: "approving-changes",
  },
  // ------------------------------------------------------------------ installer: linked accounts
  "installer.linkedAccounts": {
    title: "Linked AWS accounts",
    body: "Customer AWS accounts the installer may work in. Each link is a small bootstrap CloudFormation stack the customer creates; it holds the narrow, audited roles the installer uses. Link, then Verify.",
    example: "Link 123456789012 with Discover and Deploy, send the customer the quick-create link, and press Verify once their stack shows CREATE_COMPLETE.",
    article: "installer",
    section: "linking-an-account",
  },
  "installer.capabilities": {
    title: "Capabilities",
    body: "What the bootstrap stack lets Nexus do in the account. They are fixed when the link is created: to change them, link the account again and retire the old link.",
    example: "Discover and Deploy cover a CloudFormation install. Add Terraform for a Terraform install, and CUR reads to bill on the actual AWS bill.",
    article: "installer",
    section: "linking-an-account",
  },
  "installer.capability.discover": {
    title: "Discover",
    body: "Read-only discovery of the account and verification of the proxy. Always on in the bootstrap stack.",
    example: "The agent lists VPCs, subnets and existing nexus-proxy stacks before it proposes anything.",
    article: "installer",
    section: "linking-an-account",
  },
  "installer.capability.deploy": {
    title: "Deploy",
    body: "Lets the installer create CloudFormation change sets for nexus-proxy-* stacks (the stack's EnableDeploy switch, on by default).",
    example: "Needed for every CloudFormation install or upgrade.",
    article: "installer",
    section: "linking-an-account",
  },
  "installer.capability.terraform": {
    title: "Terraform",
    body: "Adds the Terraform runner in the customer's account: a state bucket and two CodeBuild projects (the stack's EnableTerraform switch).",
    example: "A customer that standardises on Terraform and wants the state in its own account.",
    article: "installer",
    section: "linking-an-account",
  },
  "installer.capability.cur": {
    title: "CUR reads",
    body: "A CUR 2.0 export of Bedrock rows only, and a reader role for the Nexus worker, so AWS costs can use the actual AWS bill. The stack must be in us-east-1.",
    example: "Turn it on for a customer whose private Bedrock discount should show in gross profit.",
    article: "aws-costs",
    section: "cur-sources",
  },
  "installer.externalId": {
    title: "ExternalId",
    body: "A 256-bit secret the customer's bootstrap stack requires on every assume-role call. It is shown once, right after linking, and never again, to anyone.",
    example: "The quick-create link already fills it in. If it is lost before the stack is created, unlink and link the account again.",
    article: "installer",
    section: "linking-an-account",
  },
  "installer.verified": {
    title: "Verified",
    body: "Whether Nexus has assumed the link's role and read the bootstrap stack's outputs. Only a verified link can run a session or a CUR source.",
    example: "Not verified right after linking; Verified 2026-09-24 once the customer's stack is up and you press Verify.",
    article: "installer",
    section: "linking-an-account",
  },
  "installer.templateVersion": {
    title: "Template version",
    body: "The bootstrap stack version the customer launched, read when the link is verified.",
    example: "v1.4.0. An older stack still works; a new version may grant more, which the customer adopts by updating the stack.",
    article: "installer",
    section: "linking-an-account",
  },
  // ------------------------------------------------------------------ installer: the console
  "installer.instruction": {
    title: "Instruction to the agent",
    body: "A message that steers the agent, up to 4,000 characters. It cannot write anything by itself: every change still needs your approval.",
    example: "“Use the existing VPC vpc-0abc and an internal load balancer only.”",
    article: "installer",
    section: "sessions",
  },
  "installer.budget": {
    title: "Budget",
    body: "What the session may spend on the agent's model calls, how many turns it may take, and how long it may run. Reaching any of them pauses the session until an approver extends it or someone cancels it.",
    example: "$12.40 / $100.00 · 18 / 60 turns · 1h 12m left. The defaults are $100, 60 turns and 2 hours.",
    article: "installer",
    section: "sessions",
  },
  "installer.extendUsd": {
    title: "Add dollars",
    body: "Extra model spend for this session, on top of what is left. At most $1,000 per extension; the extension is audited.",
    example: "25 adds $25.00: a paused session at $100.00 / $100.00 can continue to $125.00.",
    article: "installer",
    section: "sessions",
  },
  "installer.extendTurns": {
    title: "Add turns",
    body: "Extra agent turns for this session, 0 to 1,000. A turn is one model reply, with up to 8 tool calls.",
    example: "20 turns lifts a session paused at 60 / 60 to 60 / 80.",
    article: "installer",
    section: "sessions",
  },
  "installer.extendMinutes": {
    title: "Add minutes",
    body: "Extra wall time before the session's deadline, 0 to 1,440 (24 hours).",
    example: "60 moves a deadline of 14:00 UTC to 15:00 UTC.",
    article: "installer",
    section: "sessions",
  },
  "installer.proposal": {
    title: "Proposal",
    body: "The configuration the agent proposes for the proxy: region, IaC flavour, network, models, sign-in modes and sizing. It becomes the changes you approve; values come from the customer's account and are shown as plain data.",
    example: "Region us-east-1, CloudFormation, existing VPC vpc-0abc, internal load balancer, Auth modes aws,key.",
    article: "installer",
    section: "sessions",
  },
  "installer.billingIdentity": {
    title: "Billing identity",
    body: "Who each person's Bedrock calls are billed to in the customer's AWS billing data. Email puts each person's email there, where whoever reads the payer account's bills can see it, possibly another company. Pseudonymous puts a p- id there instead, which only the proxy's own admin People screen can resolve. It is chosen at install (pseudonymous needs the bootstrap stack at template version 4) and an upgrade keeps it, because changing it changes every person's billing id.",
    example: "A customer whose AWS bill is paid through a reseller: tell the agent “use pseudonymous billing ids”, so the reseller sees p-3f9a… rather than ana@acme.com.",
    article: "installer",
    section: "sessions",
  },
  "installer.changes": {
    title: "Changes",
    body: "What the agent wants to write, one reviewable change at a time. An install has two: the Nexus registration, then the CloudFormation change set or Terraform plan for the proxy stack.",
    example: "Nexus registration · Proposed. Open it, review the diff, tick the box and approve.",
    article: "installer",
    section: "approving-changes",
  },
  "installer.verification": {
    title: "Verification",
    body: "The steps the session has passed. Done means the proxy answered its health check and a test request, and sent a heartbeat after the apply, and the installation is registered.",
    example: "Discover, Propose and Apply ticked while it verifies: the stack is up and the installer is checking the proxy.",
    article: "installer",
    section: "sessions",
  },
  // ------------------------------------------------------------------ installer: credentials
  "installer.accessKeyId": {
    title: "Access key id",
    body: "The temporary access key for the customer's account. Only temporary keys (starting ASIA) are accepted; long-term AKIA keys are refused.",
    example: "ASIAXYZ… from the customer's `aws sts get-session-token` or their SSO console's “Access keys”.",
    article: "installer",
    section: "access-modes",
  },
  "installer.secretAccessKey": {
    title: "Secret access key",
    body: "The secret that goes with the access key. Sent once, held only in the memory of the admin task for at most an hour, and never logged or shown again.",
    example: "Paste it exactly; the form clears itself the moment you press Send credentials.",
    article: "installer",
    section: "access-modes",
  },
  "installer.sessionToken": {
    title: "Session token",
    body: "The token that makes the keys temporary. Required: without it the keys are not STS credentials.",
    example: "A long string beginning IQoJb3JpZ2luX2Vj…, copied with the key pair.",
    article: "installer",
    section: "access-modes",
  },
  "installer.idcStartUrl": {
    title: "Start URL",
    body: "The customer's IAM Identity Center sign-in address (the AWS access portal URL).",
    example: "https://acme.awsapps.com/start",
    article: "installer",
    section: "access-modes",
  },
  "installer.idcRegion": {
    title: "Region",
    body: "The AWS region the customer's IAM Identity Center instance runs in, as a region code.",
    example: "us-east-1, or eu-west-1 for a customer whose Identity Center lives in Ireland.",
    article: "installer",
    section: "access-modes",
  },
  // ------------------------------------------------------------------ installer: approving a change
  "installer.riskFlags": {
    title: "Risk flags",
    body: "What deserves a second look, computed by the server from the artifact itself, never from the agent's description. Flags never block a change.",
    example: "Internet-facing load balancer and Creates or changes an IAM role on a first install are expected; Deletes a resource on an upgrade deserves a question.",
    article: "installer",
    section: "approving-changes",
  },
  "installer.digest": {
    title: "Artifact digest",
    body: "A SHA-256 of exactly what will be applied (the change set, or the Terraform plan and its plan.json). Your approval is bound to it, and it is checked again just before anything is written.",
    example: "If the agent re-plans after you loaded the page, approving answers “the proposal changed” and you review the new diff.",
    article: "installer",
    section: "approving-changes",
  },
  "installer.reviewed": {
    title: "I reviewed the diff above",
    body: "The deliberate second step before Approve. Tick it once you have read the resource and parameter changes and the risk flags.",
    example: "Approve stays disabled until this is ticked, so a stray click cannot approve a change.",
    article: "installer",
    section: "approving-changes",
  },
  "installer.rejectReason": {
    title: "Reason for rejecting",
    body: "Why the change should not be applied, 1 to 500 characters. The session goes back to planning and the agent reads your reason.",
    example: "“Use an internal load balancer; this account must not expose the proxy to the internet.”",
    article: "installer",
    section: "approving-changes",
  },
  // ------------------------------------------------------------------ audit log
  "audit.when": {
    title: "When",
    body: "The months (UTC) to show events from. All time shows the whole trail, newest first.",
    example: "Aug 2026 – Sep 2026 shows events from Aug 1 00:00 UTC up to the end of Sep 30.",
    article: "audit-log",
    section: "what-you-see",
  },
  "audit.actor": {
    title: "Actor",
    body: "Who did it: a person's email, or the id of the user or service that acted. The match is exact.",
    example: "billing-admin@allcode.com shows only what that person changed.",
    article: "audit-log",
    section: "what-you-see",
  },
  "audit.action": {
    title: "Action",
    body: "What was done, grouped by area. You only see the areas your role may read: billing staff see billing actions, installer operators installer actions.",
    example: "period.close shows every month close, including forced ones; adjustment.approve every approved usage adjustment.",
    article: "audit-log",
    section: "what-you-see",
  },
  "audit.target": {
    title: "Target",
    body: "The thing that was changed, as its type, or its type and id separated by a slash, exactly as the Target column shows it.",
    example: "rate_plan shows events on any rate plan; rate_plan/5f1c… only that plan's.",
    article: "audit-log",
    section: "what-you-see",
  },
} satisfies Record<string, HelpTopic>;
